import ApiService from '@/common/api.service'

import * as utils from '@/common/utils'

/**
 * Returns all properties and their default state.
 *
 * @return Object
 */
function properties () {
  return {
    swapOptionSelection: 'option_0',
    clientId: null,
    replacementClientId: null,
    searchBy: 'imei',
    searchByPage3: 'imei',
    note: '',
    canProceed: true,
    oldUnit: null,
    newUnit: null,
    newVehicle: {
      id: null,
      index: null
    },
    units: [],
    newUnits: [],
    newVehicles: [],
    selectedVehicle: null,
    assignment: null,
    usedAssignments: []
  }
}

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
function defaults () {
  return {
    ...properties()
  }
}

/**
 * The module's state (VueX store object)
 */
export const state = {
  data: Object.assign({}, defaults()),
  isLoading: false
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state.data
  },

  /**
   * Returns the form's state object
   *
   * @return Object
   */
  getForm (state) {
    return state.form
  },

  /**
   * Returns true if the primary data have been modified
   * since it was originally loaded.
   *
   * @return Boolean
   */
  isModified (state) {
    return state.isModified
  },

  /**
   * Returns true if the data for the module is still being loaded.
   *
   * @return Boolean
   */
  isLoading (state) {
    return state.isLoading
  }

}

export const actions = {
  /**
   * Request a record from the API.
   *
   * @param context  Object
   * @param resource Object
   *
   * @return Promise
   */
  fetch (context, resource) {
    return ApiService
      .get(`units/by-client/${resource.id}/assigned`)
      .then((response) => {
        return response
      })
  },

  /**
   * POST (create) new record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  post (context) {
    let route
    let data

    const selectedOption = context.getters['get'].swapOptionSelection
    const oldUnit = context.getters['get'].oldUnit
    const newUnit = context.getters['get'].newUnit
    const newVehicle = context.getters['get'].newVehicle

    const replacedUnitId = oldUnit === null ? null : oldUnit.id
    const replacementUnitId = newUnit === null ? null : newUnit.id
    const replacementClientId = context.getters['get'].replacementClientId
    const vehicleId = newVehicle === null ? null : newVehicle.id
    const assignedAs = context.getters['get'].assignment

    switch (selectedOption) {
      case 'option_0':
        route = `units/swap/deallocate/unit`
        break
      case 'option_1':
        route = `units/swap/archive`
        break
      case 'option_2':
        route = `units/swap/repair`
        break
      case 'option_3':
        route = `units/swap/transfer/vehicle`
        break
      case 'option_4':
        route = `units/swap/transfer/client`
        break
      case 'option_5':
        route = `units/swap/transfer/client`
        break
    }

    if (selectedOption === 'option_0') {
      data = {
        note: context.getters['get'].note,
        unit_id: replacedUnitId,
        vehicle_id: vehicleId,
        assigned_as: assignedAs
      }
    } else {
      data = {
        note: context.getters['get'].note,
        replaced_unit_id: replacedUnitId,
        replacement_unit_id: replacementUnitId,
        replacement_client_id: replacementClientId,
        vehicle_id: vehicleId,
        assigned_as: assignedAs
      }
    }

    return ApiService
      .post(route, data)
      .then((response) => {
        context.commit('reset')
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Retrieves all units assigned to a specific client
   *
   * @param context
   * @param id
   */
  fetchUnitsByClientId (context, id) {
    return ApiService
      .get(`units/by-client/${id}/assigned`)
      .then((response) => {
        context.commit('setProp', { name: 'units', value: response.data.data })
        return response
      })
  },

  /**
   * Retrieves all units assigned to vehicle
   *
   * @param context
   * @param id
   */
  fetchUnitsAssignments (context, id) {
    return ApiService
      .get(`units/by-vehicle/${id}/assigned`)
      .then((response) => {
        context.commit('setProp', { name: 'usedAssignments', value: response.data.data })
        return response
      })
  },

  /**
   * Retrieves a vehicle with specified id.
   *
   * @param context
   * @param id
   * @returns {*}
   */
  fetchVehicleById (context, id) {
    return ApiService
      .get(`vehicles/${id}`)
      .then((response) => {
        context.commit('setProp', { name: 'selectedVehicle', value: response.data.data })
        return response
      })
  },

  /**
   * Retrieves all units that are not assigned to a vehicle
   *
   * @param context
   * @param id
   */
  fetchUnitsByClientNa (context, id) {
    return ApiService
      .get(`units/by-client/${id}/non_assigned`)
      .then((response) => {
        context.commit('setProp', { name: 'newUnits', value: response.data.data })
      })
  },

  /**
   * Retrieves all vehicles assigned to a specific client and that have
   * available assignment slots
   *
   * @param context
   * @param id
   */
  fetchVehiclesByClientId (context, id) {
    return ApiService
      .get(`vehicles/listing/by-client/${id}`)
      .then((response) => {
        context.commit('setProp', { name: 'newVehicles', value: response.data.data })
        return response
      })
  }
}

export const mutations = {
  /**
   * Set all the data properties according to the provided data.
   *
   * @param state Object
   * @param data  Object
   */
  set (state, data) {
    state.data = Object.assign({}, defaults())
  },

  setFormData (state, data) {
    state.form = {
      partners: data.partners
    }
  },

  /**
   * Sets the original primary data.
   *
   * @param state Object
   * @param data  Object
   */
  setOriginal (state, data) {
    state.original = data
  },

  /**
   * Sets the "modified" status to true or false
   *
   * @param state Object
   * @param isModified Boolean
   */
  setIsModified (state, isModified) {
    state.isModified = isModified
  },

  /**
   * Sets the "isLoading" status to true or false
   *
   * @param state Object
   * @param isLoading Boolean
   */
  setIsLoading (state, isLoading) {
    state.isLoading = isLoading
  },

  /**
   * Sets a specific property and value
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value String
   */
  setProp (state, { name, value }) {
    state.isModified = true
    utils.mutateProp(state.data, name, value)
  },

  /**
   * Pushes a new element to an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value mixed  The variable to push onto the array.
   */
  pushProp (state, { name, value }) {
    state.isModified = true
    state.data[name].push(value)
  },

  /**
   * removes an element from an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param index Number The index of the element to remove
   */
  pullProp (state, { name, index }) {
    state.isModified = true
    utils.pullProp(state.data, name, index)
  },

  /**
   * Sets the values for multiple properties.
   *
   * @param state      Object
   * @param properties Array of objects in the form { name: 'propName', value: 'xyz' }
   */
  setProps (state, properties) {
    state.isModified = true
    properties.forEach(({ name, value }) => {
      utils.mutateProp(state.data, name, value)
    })
  },

  /**
   * Resets the state.
   *
   * @param state Object
   */
  reset (state) {
    state.data = Object.assign({}, defaults())
    state.isLoading = false
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/units/swapping/unitSwappingModule.js